import React, { useState, useRef, useEffect } from "react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Sparkles, Send, X, Bot } from "lucide-react";
import { AureloMark } from "@/components/Logo";
import api, { API } from "@/lib/api";

const SUGGESTIONS = [
  "Which deals are most likely to close this month?",
  "Show me leads with high buying intent",
  "Which opportunities are at risk?",
  "Summarize today's sales activity",
];

export const RightAiPanel = ({ open, setOpen, seedMessage }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, loading]);
  useEffect(() => { if (seedMessage) { send(seedMessage); } // eslint-disable-next-line
  }, [seedMessage]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setLoading(true);
    const token = localStorage.getItem("aurelo_token");
    const tenant = localStorage.getItem("aurelo_tenant");
    const url = `${API}/ai/copilot/stream?message=${encodeURIComponent(msg)}&token=${token}&tenant_id=${tenant}`;
    let acc = "";
    setMessages((m) => [...m, { role: "ai", text: "", actions: [] }]);
    try {
      await new Promise((resolve) => {
        const es = new EventSource(url);
        es.onmessage = (e) => {
          try {
            const d = JSON.parse(e.data);
            if (d.done) { es.close(); resolve(); return; }
            if (d.delta) { acc += d.delta; setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "ai", text: acc, actions: [] }; return c; }); }
          } catch {}
        };
        es.onerror = () => { es.close(); resolve(); };
      });
      if (!acc) setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "ai", text: "Sorry, I ran into an issue. Please try again.", actions: [] }; return c; });
    } finally { setLoading(false); }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="w-full sm:max-w-[420px] p-0 flex flex-col gap-0" data-testid="ai-copilot-panel">
        <div className="flex items-center justify-between px-4 h-16 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <AureloMark size={26} />
            <div><div className="text-sm font-bold text-slate-900">Aurelo AI</div><div className="text-[11px] text-slate-400">Your revenue copilot</div></div>
          </div>
          <button onClick={() => setOpen(false)} className="h-8 w-8 rounded-lg hover:bg-slate-100 flex items-center justify-center"><X className="h-4 w-4" /></button>
        </div>
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 && (
            <div className="space-y-3">
              <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-900"><Sparkles className="h-4 w-4 text-[#7C3AED]" />Hi! I'm Aurelo AI</div>
                <p className="text-sm text-slate-500 mt-1">Ask me anything about your pipeline, leads, deals or customers.</p>
              </div>
              <div className="space-y-2">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => send(s)} className="w-full text-left text-sm rounded-xl border border-slate-200 bg-white px-3 py-2 hover:border-[#7C3AED]/40 hover:bg-slate-50 transition-colors">{s}</button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
              <div className={m.role === "user" ? "max-w-[85%] rounded-2xl rounded-br-md aurelo-gradient text-white px-3.5 py-2.5 text-sm" : "max-w-[90%] rounded-2xl rounded-bl-md bg-slate-50 border border-slate-200 px-3.5 py-2.5"}>
                {m.role === "ai" && <div className="flex items-center gap-1.5 mb-1 text-[11px] font-semibold text-[#7C3AED]"><Bot className="h-3.5 w-3.5" />Aurelo AI</div>}
                <div className={m.role === "user" ? "whitespace-pre-wrap" : "text-sm text-slate-700 whitespace-pre-wrap"}>{m.text}</div>
                {m.actions?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2.5">
                    {m.actions.map((a, j) => <span key={j} className="text-[11px] font-medium rounded-lg border border-[#7C3AED]/30 bg-white text-[#7C3AED] px-2 py-1">{a.label}</span>)}
                  </div>
                )}
              </div>
            </div>
          ))}
          {loading && (messages.length === 0 || messages[messages.length-1].text === "") && <div className="flex items-center gap-2 text-sm text-slate-400"><div className="flex gap-1"><span className="h-2 w-2 rounded-full bg-slate-300 animate-bounce" style={{animationDelay:'0ms'}}/><span className="h-2 w-2 rounded-full bg-slate-300 animate-bounce" style={{animationDelay:'150ms'}}/><span className="h-2 w-2 rounded-full bg-slate-300 animate-bounce" style={{animationDelay:'300ms'}}/></div>Thinking…</div>}
        </div>
        <div className="p-3 border-t border-slate-200">
          <div className="flex items-end gap-2 rounded-2xl border border-slate-200 bg-white p-2 focus-within:ring-2 focus-within:ring-blue-500/30">
            <textarea data-testid="ai-copilot-input" value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              rows={1} placeholder="Ask Aurelo AI…" className="flex-1 resize-none bg-transparent text-sm outline-none max-h-24 px-1 py-1" />
            <Button data-testid="ai-copilot-send-button" size="icon" onClick={() => send()} disabled={loading || !input.trim()} className="aurelo-gradient text-white h-8 w-8 shrink-0"><Send className="h-4 w-4" /></Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
